import { useMemo } from 'react';

import { useWatch, Control } from 'react-hook-form';

import { x } from '@/style';

import { Defects, RenovationPlans } from '@graphql/requests';

import { mapEnum } from '@ui-furniture/common/text-formatter';
import { Em } from '@ui-furniture/elements';

import { CreateSurveyRecommendationData } from '../common/create-recommendation-schema';

const formatDefect = mapEnum<CreateSurveyRecommendationData['defects']>({
  [Defects.None]: 'no significant',
  [Defects.Cosmetic]: 'some cosmetic',
  [Defects.Substantial]: 'substantial',
  [Defects.Major]: 'major',
});
const formatRenovation = mapEnum<
  CreateSurveyRecommendationData['renovationPlans']
>({
  [RenovationPlans.None]: 'no significant',
  [RenovationPlans.Cosmetic]: 'cosmetic',
  [RenovationPlans.Structural]: 'structural',
  [RenovationPlans.Demolition]: 'demolition',
});

export const useAdditionalDetailsSummary = (
  control: Control<CreateSurveyRecommendationData>
) => {
  const [condition, defects, renovationPlans, specialistConstruction] =
    useWatch({
      control,
      name: [
        'condition',
        'defects',
        'renovationPlans',
        'specialistConstruction',
      ],
    });
  return useMemo(() => {
    if (renovationPlans === RenovationPlans.Demolition) {
      return `You are knocking the property down so the condition of the property isn't all that important.`;
    }
    return (
      <x.div fontSize='md'>
        The property is in <Em>{condition.toLowerCase()} condition</Em>, with{' '}
        <Em>{formatDefect(defects)} defects</Em>. You plan to do{' '}
        <Em>{formatRenovation(renovationPlans)} renovation</Em> work and the
        property currently has{' '}
        <Em>
          {specialistConstruction.length ? 'some' : 'no'} specialist
          construction
        </Em>{' '}
        materials used.
      </x.div>
    );
  }, [condition, defects, renovationPlans, specialistConstruction]);
};
